/**
 * Practice Stats Store - Tracks practice mode progress with difficulty breakdown
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PracticeDifficulty } from './practiceMatchStore';

interface DifficultyStats {
  wins: number;
  losses: number;
  bestStreak: number;
  currentStreak: number;
  highestScore: number;
}

interface PracticeStats {
  // Overall stats
  wins: number;
  losses: number;
  gamesPlayed: number;
  currentStreak: number;
  bestStreak: number;
  highestScore: number;
  // Per-difficulty stats
  easy: DifficultyStats;
  medium: DifficultyStats;
  hard: DifficultyStats;
}

interface PracticeStatsState {
  stats: PracticeStats | null;
  recordResult: (won: boolean, difficulty: PracticeDifficulty, score?: number) => void;
  fetchStats: () => void;
  reset: () => void;
}

const createEmptyDifficultyStats = (): DifficultyStats => ({
  wins: 0,
  losses: 0,
  bestStreak: 0,
  currentStreak: 0,
  highestScore: 0,
});

const initialStats: PracticeStats = {
  wins: 0,
  losses: 0,
  gamesPlayed: 0,
  currentStreak: 0,
  bestStreak: 0,
  highestScore: 0,
  easy: createEmptyDifficultyStats(),
  medium: createEmptyDifficultyStats(),
  hard: createEmptyDifficultyStats(),
};

export const usePracticeStatsStore = create<PracticeStatsState>()(
  persist(
    (set, get) => ({
      stats: null,

      recordResult: (won: boolean, difficulty: PracticeDifficulty, score = 0) => {
        set((state) => {
          const current = state.stats ?? { ...initialStats };
          const diffStats = { ...current[difficulty] };

          // Update difficulty stats
          if (won) {
            diffStats.wins += 1;
            diffStats.currentStreak += 1;
            diffStats.bestStreak = Math.max(diffStats.bestStreak, diffStats.currentStreak);
          } else {
            diffStats.losses += 1;
            diffStats.currentStreak = 0;
          }
          diffStats.highestScore = Math.max(diffStats.highestScore, score);

          // Update overall stats
          const newOverallStreak = won ? current.currentStreak + 1 : 0;
          return {
            stats: {
              ...current,
              wins: current.wins + (won ? 1 : 0),
              losses: current.losses + (won ? 0 : 1),
              gamesPlayed: current.gamesPlayed + 1,
              currentStreak: newOverallStreak,
              bestStreak: Math.max(current.bestStreak, newOverallStreak),
              highestScore: Math.max(current.highestScore, score),
              [difficulty]: diffStats,
            },
          };
        });
      },

      fetchStats: () => {
        // Stats are persisted locally, just ensure they exist
        set((state) => {
          if (!state.stats) {
            return { stats: { ...initialStats } };
          }
          return state;
        });
      },

      reset: () => {
        set({ stats: { ...initialStats } });
      },
    }),
    {
      name: 'solante-practice-stats-v2',
    },
  ),
);
